import { Box, Breadcrumbs, Typography, Link } from "@mui/joy";
import { ChevronRight } from "lucide-react";
import { useLocation, Link as RouterLink } from "react-router-dom";

interface BreadcrumbItem {
    label: string;
    path: string;
}

/** Admin → Tenant → Organizations → Organization name */
function buildBreadcrumbs(pathname: string, organizationName?: string): BreadcrumbItem[] {
    const items: BreadcrumbItem[] = [
        { label: "Admin", path: "/admin" },
        { label: "Tenant", path: "/admin/organizations/list" },
        { label: "Organizations", path: "/admin/organizations/list" },
    ];

    const segments = pathname.split("/").filter(Boolean);
    const organizationsIndex = segments.indexOf("organizations");

    if (organizationsIndex === -1 || organizationsIndex === segments.length - 1) {
        return items;
    }

    const actionSegment = segments[organizationsIndex + 1];

    if (actionSegment === "create") {
        items.push({ label: "Create", path: pathname });
    } else if (actionSegment && actionSegment !== "list") {
        items.push({ label: organizationName || "Organization", path: pathname });
    }

    return items;
}

export default function Breadcrumb({ organizationName }: { organizationName?: string }) {
    const location = useLocation();
    const breadcrumbs = buildBreadcrumbs(location.pathname, organizationName);

    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 0, ml: 1, mr: 2 }}
        >
            <Breadcrumbs separator={<ChevronRight size={18} />}>
                {breadcrumbs.map((item, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <Box key={`${item.path}-${item.label}`}>
                            {isLast ? (
                                <Typography
                                    level={index === 0 ? "title-lg" : "body-md"}
                                    sx={{ fontWeight: 500 }}
                                >
                                    {item.label}
                                </Typography>
                            ) : (
                                <Link
                                    component={RouterLink}
                                    to={item.path}
                                    level={index === 0 ? "title-lg" : "body-md"}
                                    sx={{
                                        fontWeight: 500,
                                        textDecoration: "none",
                                        color: "inherit",
                                        "&:hover": {
                                            textDecoration: "underline",
                                        },
                                    }}
                                >
                                    {item.label}
                                </Link>
                            )}
                        </Box>
                    );
                })}
            </Breadcrumbs>
        </Box>
    );
}
